import { LazyLoader } from '@bp/ui-components';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { UserConfigStore } from '../../stores/userConfig/UserConfigStore';

export const UserConfigContext = createContext<UserConfigStore>({} as UserConfigStore);
export const userConfigStore = new UserConfigStore();

/**
 * Laedt die persoenliche Konfiguration einmalig und stellt sie bereit.
 *
 * Das Laden passiert im Effekt und nicht im Render: `load()` schreibt unter
 * Umstaenden (erstes Schuljahr), und ein Aufruf im Render wuerde das bei jedem
 * Re-Render erneut tun. Der Store selbst cacht sein Promise, sodass auch
 * StrictMode-Doppelaufrufe nur einmal laden.
 *
 * Ohne Profil-UUID und im Fehlerfall wird trotzdem gerendert — der Store faellt
 * dann auf seine Defaults zurueck. Endlos im Loader haengen bleibt die Seite nie.
 */
export function UserConfigProvider({ children }: { children: ReactNode }) {
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const [loading, setLoading] = useState(true);

  // pimAuthClaims wird bei jedem Render neu gebaut; die Identitaet des Ladevorgangs ist die Profil-UUID.
  //biome-ignore lint/correctness/useExhaustiveDependencies(pimAuthClaims): siehe oben
  useEffect(() => {
    if (!profileUuid) {
      setLoading(false);
      return;
    }

    let active = true;
    void userConfigStore.load(pimAuthClaims).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [profileUuid]);

  if (loading) {
    return <LazyLoader embedded forceHeight='100vh' forceWidth='100vw' size='xxl' />;
  }

  return <UserConfigContext.Provider value={userConfigStore}>{children}</UserConfigContext.Provider>;
}
